import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  requiredRole?: "seller" | "admin";
}

/**
 * Role hierarchy: user < seller < admin.
 * - requiredRole="seller" → allows seller and admin
 * - requiredRole="admin"  → allows admin only
 */
export default function ProtectedRoute({ requiredRole }: Props) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  // Still loading user data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="space-y-4 w-64">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-8 w-1/2" />
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check
  if (requiredRole) {
    const allowedRoles: string[] =
      requiredRole === "seller"
        ? ["seller", "admin"]
        : ["admin"];
    if (!user || !allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
  }

  return <Outlet />;
}
